import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreakpointObserver, Breakpoints, LayoutModule } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { NavigationService, NavigationItem } from '../../services/navigation.service';
import { VerticalNavItemComponent } from './vertical-nav-item/vertical-nav-item.component';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';

@Component({
  selector: 'lib-vertical-nav',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    LayoutModule,
    VerticalNavItemComponent,
    LanguageSwitcherComponent
  ],
  templateUrl: './vertical-nav.component.html',
  styleUrl: './vertical-nav.component.scss'
})
export class VerticalNavComponent implements OnInit, OnDestroy {
  @Input() title = 'Smart Parking';
  @Input() logoUrl = 'assets/logo-jjames.webp';
  @Input() autoCollapse = true; // Auto-collapse on mobile
  @Input() navigationItems: NavigationItem[] = [];
  @Input() environment: any;
  /** Opt-in: render the language switcher in the footer (requires the app to provide TranslateService). */
  @Input() showLanguageSwitcher = false;
  isCollapsed = false;
  isHandset = false;

  private destroy$ = new Subject<void>();
  private breakpointObserver = inject(BreakpointObserver);
  navigationService = inject(NavigationService); // Made public for template access
  router = inject(Router);

  ngOnInit(): void {
    this.setupBreakpoints();
    this.setupNavigation();
  }

  toggleCollapse(): void {
    this.navigationService.setCollapse(!this.isCollapsed);
  }

  navigateTo(route: string): void {
    this.navigationService.navigateTo(route);
    // Auto-collapse on mobile after navigation
    if (this.isHandset) {
      this.navigationService.setCollapse(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupBreakpoints(): void {
    this.breakpointObserver.observe([Breakpoints.Handset])
      .pipe(
        takeUntil(this.destroy$),
        map(result => result.matches)
      )
      .subscribe(isHandset => {
        this.isHandset = isHandset;
        // Always collapse on mobile devices
        if (isHandset) {
          this.navigationService.setCollapse(true);
        }
      });
  }

  private setupNavigation(): void {
    // Subscribe to navigation items
    this.navigationService.getNavigationItems().pipe(
      takeUntil(this.destroy$)
    ).subscribe(items => {
      this.navigationItems = items;
    });

    // Subscribe to collapse state
    this.navigationService.getCollapseState().pipe(
      takeUntil(this.destroy$)
    ).subscribe(collapsed => {
      this.isCollapsed = collapsed;
      // Collapse all expanded items when navigation is collapsed
      if (collapsed) {
        this.collapseAllItems();
      }
    });
  }

  private collapseAllItems(): void {
    const collapseRecursive = (items: NavigationItem[]) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          item.expanded = false;
          collapseRecursive(item.children);
        }
      });
    };
    collapseRecursive(this.navigationItems);
  }
}